import { requestCall } from '@/app/store/commonSlice';
import { CopyIcon, IconIncomingCallIcon, IconSelectTick, ProhibitIcon } from '@/assets/images';
import { AppIcon, ListTile, SearchBox, SmartListView } from '@/components';
import { resetAlertModal, setAlertModal } from '@/components/AlertModalRoot/alertModalService';
import EmptyPlaceholder from '@/components/EmptyPlaceholder';
import ListTileSkeleton from '@/components/ListTile/ListTileSkeleton';
import { clearSearchCache } from '@/components/SearchBox';
import SelectedUserChips from '@/features/chats/sub-features/group-chat/ui/SelectedUserChips';
import { formatJidToUserId, formatUserIdToJid } from '@/features/chats/utils';
import { useGroupParticipants } from '@/features/chats/viewmodels';
import { ExternalBadge } from '@/features/invite/ui';
import { UserProfile, Username } from '@/features/users';
import { useIsBlockedByMe, useUserDetails } from '@/features/users/model/selectors';
import { useUsersListViewModel } from '@/features/users/usersList/viewmodel/useUsersListViewModel';
import { blockContactAction } from '@/features/users/utils/userBlockAction';
import { i18n } from '@/localization/i18n';
import { useLanguage } from '@/localization/useLanguage';
import { CALL_TYPE } from '@/shared/constants/callConstants';
import { CALLKIT_CALLSTATUS } from '@/features/calls/constants';
import { getLocalUserId } from '@/shared/utils';
import { blockOfflineAction } from '@/shared/utils/network';
import { showInfoToast } from '@/shared/utils/toastMessage';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from '@/config';
import styles from './GroupCallSelectionPopup.module.scss';

const ParticipantItem = ({ p, isSelected, onToggle, t }) => {
   const isBlockedByMe = useIsBlockedByMe(p.userId);
   const { status, nickName, isExternal } = useUserDetails(p.userId, ['status', 'nickName', 'isExternal']);

   const onUnblock = e => {
      e.stopPropagation();
      if (blockOfflineAction()) return;
      const jid = formatUserIdToJid(p.userId);
      const displayName = nickName || p.userId;
      const modalId = 'unblockContactModal';
      // Build the confirmation dialog directly using data already available from hooks.
      // handleToggleBlockClick was silently crashing because getUserDetails() returns
      // null when the user profile isn't loaded in the store at the time of the click.
      setAlertModal({
         id: modalId,
         visible: true,
         icon: <AppIcon icon={<ProhibitIcon className="danger-icon" />} />,
         title: i18n.t('chat.UNBLOCK_CONFIRM', { name: displayName }),
         description: i18n.t('chat.UNBLOCK_DESC'),
         yesButton: i18n.t('chat.UNBLOCK'),
         noButton: i18n.t('common.CANCEL'),
         yesAction: () => {
            blockContactAction(jid, true);
            resetAlertModal(modalId);
         },
         noAction: () => resetAlertModal(modalId),
         onRequestClose: () => resetAlertModal(modalId),
         customClassName: 'delete-account-confirm-modal alert-modal-delete-v2',
      });
   };

   return (
      <ListTile
         key={p.userId}
         onClick={() => !isBlockedByMe && onToggle(p.userId)}
         avatar={<UserProfile userId={p.userId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />}
         title={<Username userId={p.userId} />}
         subTitle={<span className={styles.userStatusText}>{status || t('chat.MIRRORFLY_TEAMS_STATUS')}</span>}
         trailing={
            <div className="ct-flex ct-aiCenter ct-gap8">
               <ExternalBadge isExternalUser={isExternal} className="external-badge--compact" />
               {isBlockedByMe ? (
                  <button className={styles.unblockBtn} onClick={onUnblock}>
                     {t('chat.UNBLOCK')}
                  </button>
               ) : (
                  <div
                     className={`ct-flex ct-aiCenter ct-jcCenter ${styles.checkboxCircle} ${isSelected ? styles.selected : ''}`}
                  >
                     {isSelected && <IconSelectTick color="#fff" size={12} strokeWidth={3} />}
                  </div>
               )}
            </div>
         }
         className={`${isSelected ? 'selected' : ''} ${isBlockedByMe ? styles.blockedUser : ''}`}
      />
   );
};

ParticipantItem.propTypes = {
   p: PropTypes.shape({
      userId: PropTypes.string,
   }),
   isSelected: PropTypes.bool,
   onToggle: PropTypes.func,
   t: PropTypes.func,
};

const GroupCallSelectionPopup = ({ activeChatJid, callType, onClose, mode = 'call', existingUsers = [], linkOnly = false }) => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const groupId = formatJidToUserId(activeChatJid);
   const [searchQuery, setSearchQuery] = useState('');
   const { participants } = useGroupParticipants(groupId, searchQuery);
   const localUserId = getLocalUserId();
   const [selectedUsers, setSelectedUsers] = useState([]);
   const { callLink, ongoingCallParticipants = [] } = useSelector(state => state.callkitData || {});
   const [isCopied, setIsCopied] = useState(false);
   const handleClose = useCallback(() => {
      clearSearchCache('groupCallSearch');
      onClose();
   }, [onClose]);

   const handleCopyLink = () => {
      if (callLink) {
         navigator.clipboard.writeText(callLink);
         showInfoToast(t('common.LINK_COPIED'));
         setIsCopied(true);
         setTimeout(() => setIsCopied(false), 2000);
      }
   };

   // Only exclude participants who are actively in / being connected to the call.
   // Terminal statuses (TIMEOUT, DISCONNECTED, REJECTED, ENGAGED) mean the user is no
   // longer reachable — treat them as available so the invite list updates immediately
   // when the status changes, without waiting for removeParticipantFromCall to fire.
   const activeParticipantIds = useMemo(() => {
      const terminalStatuses = new Set([
         CALLKIT_CALLSTATUS.DISCONNECTED,
         CALLKIT_CALLSTATUS.TIMEOUT,
         CALLKIT_CALLSTATUS.REJECTED,
         CALLKIT_CALLSTATUS.ENGAGED,
      ]);
      return (ongoingCallParticipants || []).filter(p => !terminalStatuses.has(p.callStatus)).map(p => p.userId);
   }, [ongoingCallParticipants]);

   const {
      filteredUsers: globalUsers,
      loading,
      loadMore,
      hasMore,
      handleSearchChange,
   } = useUsersListViewModel({
      excludedUsers: useMemo(
         () => Array.from(new Set([...existingUsers, ...activeParticipantIds])),
         [existingUsers, activeParticipantIds],
      ),
   });

   useEffect(() => {
      return () => {
         clearSearchCache('groupCallSearch');
      };
   }, []);

   useEffect(() => {
      if (selectedUsers.length > 0) {
         // Remove chips only for participants actively in the call — not for timed-out users
         setSelectedUsers(prev => prev.filter(userId => !activeParticipantIds.includes(userId)));
      }
   }, [activeParticipantIds]);

   const onSearchChange = useCallback(
      query => {
         setSearchQuery(query);
         if (!activeChatJid) {
            handleSearchChange(query);
         }
      },
      [activeChatJid, handleSearchChange],
   );

   useEffect(() => {
      // Trigger initial load if needed and not already searching or loading
      if (!activeChatJid && !loading && hasMore && globalUsers.length === 0) {
         loadMore();
      }
   }, [activeChatJid, loading, hasMore, globalUsers.length, loadMore]);

   const filteredParticipants = useMemo(() => {
      // Use activeParticipantIds (terminal statuses excluded) so the list updates the
      // moment a participant's status becomes TIMEOUT/DISCONNECTED/REJECTED/ENGAGED,
      // without waiting for removeParticipantFromCall to fire on the next tick.
      const allExcluded = Array.from(new Set([...existingUsers, ...activeParticipantIds]));

      if (!activeChatJid) {
         // Apply allExcluded directly here as a synchronous guard — globalUsers from
         // useUsersListViewModel goes through a multi-hop useMemo chain and may lag
         // one render cycle behind the Redux change.
         return globalUsers.filter(p => p.userId !== localUserId && !allExcluded.includes(p.userId));
      }
      return (participants || []).filter(p => p.userId !== localUserId && !allExcluded.includes(p.userId));
   }, [participants, globalUsers, localUserId, existingUsers, activeChatJid, activeParticipantIds]);

   const toggleUser = useCallback(
      userId => {
         const baseCount =
            mode === 'call' ? 1 : ongoingCallParticipants.filter(p => !p.userId.endsWith('-screen')).length;
         const isSelected = selectedUsers.includes(userId);
         if (!isSelected && baseCount + selectedUsers.length >= config.maxCallParticipants) {
            showInfoToast(t('calls.MAX_LIMIT_REACHED', { count: config.maxCallParticipants }));
            return;
         }
         setSelectedUsers(prev => (isSelected ? prev.filter(id => id !== userId) : [...prev, userId]));
      },
      [selectedUsers, ongoingCallParticipants, t, mode],
   );

   const handleCall = () => {
      if (blockOfflineAction()) return;
      if (selectedUsers.length === 0) return;

      const baseCount = mode === 'call' ? 1 : ongoingCallParticipants.filter(p => !p.userId.endsWith('-screen')).length;
      if (baseCount + selectedUsers.length > config.maxCallParticipants) {
         showInfoToast(t('calls.MAX_LIMIT_REACHED', { count: config.maxCallParticipants }));
         return;
      }

      const isVideoCall = callType === CALL_TYPE.VIDEO;
      if (mode === 'invite') {
         dispatch(requestCall({ userIds: selectedUsers, callType, isInvite: true }));
      } else {
         dispatch(requestCall({ userIds: selectedUsers, groupId, isVideoCall, callType, isGroup: true }));
      }
      handleClose();
   };

   const renderItem = useCallback(
      ({ item: p }) => (
         <ParticipantItem p={p} isSelected={selectedUsers.includes(p.userId)} onToggle={toggleUser} t={t} />
      ),
      [selectedUsers, toggleUser, t],
   );

   const keyExtractor = useCallback(item => item.userId, []);

   const renderParticipants = () => {
      if (loading && filteredParticipants.length === 0) {
         return (
            <div className={`${styles.loaderContainer} ct-flex ct-fcol ct-flex1`}>
               {[...Array(8)].map((_, i) => (
                  <ListTileSkeleton key={`skele-v-${i * 2 + 1}`} />
               ))}
            </div>
         );
      }
      if (filteredParticipants.length > 0) {
         return (
            <SmartListView
               id="group-call-participants-list"
               data={filteredParticipants}
               renderItem={renderItem}
               keyExtractor={keyExtractor}
               itemHeight={52}
               onLoadBottom={!activeChatJid ? loadMore : undefined}
               hasMoreBottom={!activeChatJid ? hasMore : false}
               loading={!activeChatJid ? loading : false}
            />
         );
      }
      return <EmptyPlaceholder title={t('common.NO_PARTICIPANTS_FOUND')} />;
   };

   // MEET mode: show only the shareable link — no user search, no invite button
   if (linkOnly) {
      return (
         <div className={`${styles.groupCallModalContainer} ct-flex ct-fcol`}>
            {callLink ? (
               <div className={styles.groupCallLinkContainer}>
                  <p>{t('chat.CALL_LINK')}</p>
                  <div className={`${styles.linkBox} ct-flex ct-aiCenter ct-jcBetween`}>
                     <span className={`${styles.linkText} ct-flex1`} title={callLink}>
                        {callLink}
                     </span>
                     <button
                        className={`${styles.copyBtn} ct-flexCenter`}
                        onClick={handleCopyLink}
                        title={t('common.COPY_LINK')}
                     >
                        <i className="ct-icon sm">
                           {isCopied ? <IconSelectTick style={{ color: '#00c853' }} /> : <CopyIcon />}
                        </i>
                     </button>
                  </div>
               </div>
            ) : (
               <EmptyPlaceholder title={t('toast.SOMETHING_WENT_WRONG_RETRY')} />
            )}
         </div>
      );
   }

   return (
      <div className={`${styles.groupCallModalContainer} ct-flex ct-fcol`}>
         {callLink && (
            <div className={styles.groupCallLinkContainer}>
               <p>{t('chat.CALL_LINK')}</p>
               <div className={`${styles.linkBox} ct-flex ct-aiCenter ct-jcBetween`}>
                  <span className={`${styles.linkText} ct-flex1`} title={callLink}>
                     {callLink}
                  </span>
                  <button
                     className={`${styles.copyBtn} ct-flexCenter`}
                     onClick={handleCopyLink}
                     title={t('common.COPY_LINK')}
                  >
                     <i className="ct-icon sm">
                        {isCopied ? <IconSelectTick style={{ color: '#00c853' }} /> : <CopyIcon />}
                     </i>
                  </button>
               </div>
            </div>
         )}

         <div className={styles.searchSection}>
            <div className="callScreenSearchbox">
               <SearchBox scopeKey="groupCallSearch" onChange={onSearchChange} placeholder={t('common.SEARCH')} />
            </div>
            <SelectedUserChips selectedUsers={selectedUsers} onRemove={toggleUser} />
         </div>

         <div className={`${styles.groupCallParticipantList} ct-flex1 ct-flex ct-fcol`}>{renderParticipants()}</div>

         <div className={`${styles.groupCallFooter} ct-flex ct-jcCenter`}>
            <button
               className={`${styles.groupCallBtn} ${mode === 'invite' ? styles.inviteBtn : 'ct-wFull'} ct-flexCenter ct-gap8`}
               disabled={selectedUsers.length === 0}
               onClick={handleCall}
            >
               {mode === 'invite' ? (
                  <span>{t('chat.INVITE')}</span>
               ) : (
                  <>
                     <i className="ct-icon">
                        <IconIncomingCallIcon />{' '}
                     </i>
                     <span>{t('chat.CALL_NOW', { count: selectedUsers.length })}</span>
                  </>
               )}
            </button>
         </div>
      </div>
   );
};

GroupCallSelectionPopup.propTypes = {
   activeChatJid: PropTypes.string,
   callType: PropTypes.oneOf([CALL_TYPE.AUDIO, CALL_TYPE.VIDEO]).isRequired,
   onClose: PropTypes.func.isRequired,
   mode: PropTypes.oneOf(['call', 'invite']),
   existingUsers: PropTypes.arrayOf(PropTypes.string),
   linkOnly: PropTypes.bool,
};

export default GroupCallSelectionPopup;
